import React, {useEffect, useState} from "react";
import type {ContextProps} from '@/types';
import {PageTitle} from "@components/Landmarks";
import {GridCell, GridContainer} from "@components/Grid";
import {Button, SelectField} from "@components/Inputs";
import {isNullOrUndefined} from "@utilities/commonUtilities";
import {
    getPrinterConnectionStatus,
    getPrinterName,
    getUpdatedLabelXml,
    NO_PRINTERS_FOUND,
    PRINTER_NOT_CONNECTED
} from "./dymoPrintLabelUtilities";
import {HtmlDisplay} from "@components/Display";
import {LayoutFullBleed, LayoutSmall} from "@components/Layout";
import {libraryCatalogApi} from "../Library/libraryCatalogApi";
import {useInput} from "@components/Inputs/inputUtility";
import {createListFromArray} from "@utilities/Types/listUtilities";
// @ts-ignore
import Dymo from "dymojs";

export const DymoPrintLabelContainer = ({
                                            catalogCopyId,
                                            handleApiCall,
                                            handleReturnRedirect,
                                            setWarnings,
                                        }: ContextProps) => {

    const barcodeInput = useInput("");

    const [printers, setPrinters] = useState<string | null>(null);
    const [copyDetails, setCopyDetails] = useState<Record<string, unknown> | null>(null);
    const [imageData, setImageData] = useState("");
    const [labelXml, setLabelXml] = useState<string | null>(null);
    const [error, setError] = useState("");

    const dymo = new Dymo();

    const connectedDymoPrinterName = !isNullOrUndefined(printers) ? getPrinterName(printers) : NO_PRINTERS_FOUND;
    const printerConnectionStatus = !isNullOrUndefined(printers) ? getPrinterConnectionStatus(printers) : PRINTER_NOT_CONNECTED;

    const noDymoPrinters = isNullOrUndefined(printers) || connectedDymoPrinterName === NO_PRINTERS_FOUND;
    const printerNotConnected = printerConnectionStatus === PRINTER_NOT_CONNECTED;

    const noCopyDetails = !copyDetails;

    const getPrinters = async () => {
        try {
            const printersXml = await dymo.getPrinters();
            setPrinters(printersXml);
            const status = getPrinterConnectionStatus(printersXml);
            if (status === PRINTER_NOT_CONNECTED) {
                setWarnings("No DYMO Printer is connected. Please check your printer connection and try again.");
            }

        } catch (errorDetails) {
            setError("Error finding DYMO printers. " + errorDetails);
        }
    }

    const sendToPrinter = async (updatedLabelXml: string) => {
        try {
            setError("");
            await getPrinters();

            const result = await dymo.print(connectedDymoPrinterName, updatedLabelXml);
            if (result && result.includes("is not found")) {
                setError("No DYMO printer is found. Please check your printer connection and try again.");
            } else if (!result) {
                setError("Error during label printing: " + result);
            }
        } catch (errorDetails) {
            setError("Exception during label printing: " + errorDetails);
        }
    }

    const setupLabelPreview = async (updatedLabelXml: string) => {
        try {
            const labelData = await dymo.renderLabel(updatedLabelXml);
            const imageData = `data:image/png;base64, ` + labelData.replace(/"/g, "").replace(/\\/g, "");
            setImageData(imageData);
        } catch (errorDetails) {
            setError("Error building preview: " + errorDetails);
        }
    }

    const handleReturn = () => handleReturnRedirect();

    const handlePrintClick = () => {
        if (labelXml) sendToPrinter(labelXml).then();
    };

    useEffect(() => {
        if (noCopyDetails)
            handleApiCall(() => libraryCatalogApi.getCopyBarcode(catalogCopyId), setCopyDetails);
    }, [catalogCopyId]);

    useEffect(() => {
        if (labelXml)
            setupLabelPreview(labelXml).then();
    }, [labelXml]);

    useEffect(() => {
        if (copyDetails) {
            const details = copyDetails as Record<string, any>;
            const dynamicMainTitle = details.title;
            const dynamicDetails = details.details;
            const dynamicBarcodeText = barcodeInput.value || details.barcodeList[0];

            let updatedLabelXml = getUpdatedLabelXml({dynamicMainTitle, dynamicDetails, dynamicBarcodeText});
            setLabelXml(updatedLabelXml);
        }
    }, [copyDetails, barcodeInput.value]);

    useEffect(() => {
        if (!printers) {
            getPrinters().then();
        }
    }, []);

    if (!copyDetails) return null;
    const barcodeList = createListFromArray((copyDetails as Record<string, any>).barcodeList);

    return <>
        <PageTitle h1={`Print Label`}/>
        <LayoutFullBleed className={`catalog text-center`}>
            {
                error &&
                <HtmlDisplay html={`<p class="alert-color">Warning: <b>${error}</b></p>`}/>
            }
            {
                noCopyDetails &&
                <HtmlDisplay html={`<p class="alert-color">Warning: <b>No details found.</b></p>`}/>
            }
            {
                !noDymoPrinters &&
                <HtmlDisplay
                    html={`<p>Available Dymo Printer: <b>${connectedDymoPrinterName}</b>  Connection Status: <b>${printerConnectionStatus}</b></p>`}/>
            }
            {
                noDymoPrinters && !noCopyDetails && !error &&
                <HtmlDisplay html={`<p>Available Dymo Printer: <b>${NO_PRINTERS_FOUND}</b></p>`}/>
            }
            {
                imageData &&
                <p><img src={imageData} alt={`label preview`}/></p>
            }

            <hr/>
            <LayoutSmall>
                <GridContainer>
                    <SelectField
                        includeDefaultOption={false}
                        label={`Volume Access Number`}
                        name={`sfBarcode`}
                        options={barcodeList}
                        {...barcodeInput}
                    />
                    <GridCell>
                        <Button
                            isPrimary isCollapsed
                            label={'Print Label'}
                            name={`btnPrintLabel`}
                            disabled={noDymoPrinters || printerNotConnected || noCopyDetails}
                            onClick={handlePrintClick}/>
                        <Button
                            isPrimary isCollapsed
                            label={'Search for Printers'}
                            name={`btnSearchPrinters`}
                            onClick={getPrinters}/>
                        <Button
                            isCollapsed
                            label={'Cancel'}
                            name={`btnCancel`}
                            onClick={handleReturn}/>
                    </GridCell>
                </GridContainer>
            </LayoutSmall>
        </LayoutFullBleed>

    </>;
};
